
import {
  createRouter,
  createWebHistory,
  RouteRecordRaw,
  RouteLocationNormalized,
  RouteRecordNormalized,
} from "vue-router";
import PublicLayout from "../views/layouts/Public.vue";
import EntityIndex from "../views/EntityIndex.vue";
import Home from "../views/Home.vue";
import News from "../views/News.vue";
import ShowNew from "../views/ShowNew.vue";
import Videos from "../views/Videos.vue";
import MonthlyReport from "../views/MonthlyReport.vue";
import ShowVideo from "../views/ShowVideo.vue";
import RegisterUser from "../views/RegisterUser.vue";
import EntityProfile from "../views/EntityProfile.vue";
import ForgotPassword from "../views/admin/ForgotPassword.vue";
import ResetPassword from "../views/admin/ResetPassword.vue";
import EmailVerification from "../views/EmailVerification.vue";
import EmailVerificationMessage from "../views/VerifyEmailMessage.vue";
import { monthPeriodParser, numericParser } from "@/router/parsers";
import { Vue } from "vue-class-component";
import {
  authenticationMiddleware,
  authorizationMiddleware,
  EnsureAuthDataValid,
  Middleware,
  MiddlewareChain,
} from "./middleware";
import logger from "@/logger";
import EntityRoutes from "./routes/entity";
import UserRoutes from "./routes/user";
import store from "@/store";
import { StoreState } from "@/shims-vuex";
import { Store } from "vuex";

const Transparency = () =>
  import(/* webpackChunkName: "transparency" */ "../views/Transparency.vue");
const DocumentationSubmissionViewer = () =>
  import(
    /* webpackChunkName: "transparency" */ "@/components/transparency/DocumentationSubmissionViewer.vue"
  );

const Login = () =>
  import(/* webpackChunkName: "login" */ "@/views/admin/Login.vue");

let appVm: Vue;
const log = logger.extend("router");

const routes: Array<RouteRecordRaw> = [
  {
    /***  Public Routes ***/
    path: "/",
    component: PublicLayout,
    children: [
      {
        path: "/",
        name: "home",
        component: Home,
      },
      {
        path: "transparencia/:entity_id/:period?",
        component: Transparency,
        name: "entity-transparency",
        meta: {
          title: "Transparencia de la entidad",
        },
        children: [
          {
            name: "submission-view",
            path: "entregas/:entityDocumentationId",
            component: DocumentationSubmissionViewer,
            props: (route) => ({
              ...monthPeriodParser(route, "period"),
              ...numericParser(route, "entityDocumentationId"),
            }),
          },
        ],
      },
      {
        path: "entidades",
        component: EntityIndex,
        name: "entity-index",
      },
      {
        path: "noticias",
        component: News,
        name: "public-news",
      },
      {
        path: "noticias/:newsId",
        component: ShowNew,
        name: "public-news-show",
        props: true,
      },
      {
        path: "videos",
        component: Videos,
        name: "public-videos",
      },
      {
        path: "video/:videoId",
        component: ShowVideo,
        name: "public-video-show",
        props: true,
      },
      {
        path: "informes-mensuales",
        component: MonthlyReport,
        name: "public-reports",
      },
      {
        path: "validacion",
        component: () => import("../views/admin/certification/Index.vue"),
        name: "public-certification",
        props: () => ({
          public: true,
        }),
      },
      // Disabled for the moment
      {
        path: "perfil-entidad/:entityId",
        component: EntityProfile,
        name: "public-entity-profile",
        props: true,
      },

      {
        path: "validacion/:entityId/periodo/:periodYear",
        component: () =>
          import(
            /* webpackChunkName: "admin" */ "../views/admin/certification/CertificationEntity.vue"
          ),
        name: "public-certification-show",
        props: (route) => ({
          public: true,
          entityId: 4,
          ...numericParser(route, "entityId"),
          ...numericParser(route, "periodYear"),
        }),
      },
      // Disabled for the moment
      {
        path: "registrarse",
        component: RegisterUser,
        name: "public-registration",
        props: () => ({
          public: true,
        }),
      },
    ],
  } /***  End Of Public Routes ***/,

  {
    path: "/login",
    name: "login",
    component: Login,
  },
  {
    path: "/forgot-password",
    name: "forgot-password",
    component: ForgotPassword,
  },
  {
    path: "/reset-password/:token",
    name: "reset-password",
    component: ResetPassword,
    props: (route) => ({
      email: route.query.email,
      token: route.params.token,
    }),
  },
  {
    path: "/verificacion-correo",
    name: "email-verification-message",
    component: EmailVerificationMessage,
  },
  {
    path: "/verify-email",
    name: "email-verification",
    component: EmailVerification,
    props: (route) => ({
      verified: route.query.verified,
    }),
  },
  {
    path: "/citizen",
    name: "citizen",
    meta: {
      middlewares: [new EnsureAuthDataValid(), authenticationMiddleware],
    },
    component: () =>
      import(/* webpackChunkName: "admin" */ "../views/layouts/Admin.vue"), // using the same from admin
    children: [
      {
        path: "",
        name: "citizen-dashboard",
        component: () =>
          import(
            /* webpackChunkName: "admin" */ "../views/citizen/Dashboard.vue"
          ),
        props: () => ({
          authUserRole: appVm.$store.state.authUser.role,
          authUser: appVm.$store.state.authUser.user,
        }),
      },

      {
        path: "crear-solicitud",
        name: "citizen-information-request-create",
        component: () =>
          import(
            /* webpackChunkName: "admin" */ "../views/citizen/requests/Create.vue"
          ),
        props: true,
        meta: {
          middlewares: [authorizationMiddleware],
        },
      },
      /**
       *  Edit a request
       */
      {
        path: ":requestId/editar-solicitud",
        name: "citizen-information-request-edit",
        component: () =>
          import(
            /* webpackChunkName: "admin" */ "../views/citizen/requests/Edit.vue"
          ),
        props: true,
        meta: {
          middlewares: [authorizationMiddleware],
        },
      },
      {
        path: ":requestId/eliminar-solicitud",
        name: "citizen-information-request-delete",
        component: () =>
          import(
            /* webpackChunkName: "admin" */ "../views/citizen/requests/Delete.vue"
          ),
        props: true,
        meta: {
          middlewares: [authorizationMiddleware],
        },
      },
      /**
       * View Response
       */
      {
        path: ":requestId/ver-solicitud",
        name: "citizen-information-request-view",
        component: () =>
          import(
            /* webpackChunkName: "admin" */ "../views/citizen/requests/View.vue"
          ),
        props: true,
        meta: {
          middlewares: [authorizationMiddleware],
        },
      },
    ],
  },
  {
    /***  Private Routes ***/
    path: "/admin",
    name: "Admin",
    meta: {
      middlewares: [new EnsureAuthDataValid(), authenticationMiddleware],
    },
    component: () =>
      import(/* webpackChunkName: "admin" */ "../views/layouts/Admin.vue"),
    children: [
      {
        path: "",
        name: "admin-dashboard",
        component: () =>
          import(
            /* webpackChunkName: "admin" */ "../views/admin/Dashboard.vue"
          ),
        props: () => ({
          authUserRole: appVm.$store.state.authUser.role,
          authUser: appVm.$store.state.authUser.user,
        }),
      },
      {
        path: "mostrar-documentacion",
        name: "admin-dashboard-documentation",
        component: () => import("../views/admin/entity/ShowDocumentation.vue"),
      },
      {
        path: ":documentId/editar-documentacion",
        name: "admin-documentation-edit",
        component: () =>
          import(
            /* webpackChunkName: "admin" */ "../views/admin/entity/EditDocumentation.vue"
          ),
        props: true,
        meta: {
          middlewares: [authorizationMiddleware],
        },
      },
      {
        path: "mostrar-noticias",
        name: "admin-show-news",
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "news/view",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () => import("../views/admin/news/Index.vue"),
      },
      {
        path: "crear-noticias",
        name: "admin-new-create",
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "news/create",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () => import("../views/admin/news/Create.vue"),
      },
      {
        path: ":newId/editar-noticia",
        name: "admin-new-edit",
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "news/edit",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () =>
          import(
            /* webpackChunkName: "admin" */ "../views/admin/news/Edit.vue"
          ),
        props: true,
      },
      {
        path: ":newId/eliminar-noticia",
        name: "admin-new-delete",
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "news/delete",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () =>
          import(
            /* webpackChunkName: "admin" */ "../views/admin/news/Delete.vue"
          ),
        props: true,
      },
      {
        path: "mostrar-videos",
        name: "admin-show-videos",
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "video/view",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () => import("../views/admin/video/Index.vue"),
      },
      {
        path: "crear-video",
        name: "admin-video-create",
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "video/create",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () => import("../views/admin/video/Create.vue"),
      },
      {
        path: ":videoId/editar-video",
        name: "admin-video-edit",
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "video/edit",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () =>
          import(
            /* webpackChunkName: "admin" */ "../views/admin/video/Edit.vue"
          ),
        props: true,
      },
      {
        path: ":videoId/eliminar",
        name: "admin-video-delete",
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "video/delete",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () =>
          import(
            /* webpackChunkName: "admin" */ "../views/admin/video/Delete.vue"
          ),
        props: true,
      },
      {
        path: "validaciones",
        name: "admin-show-certification",
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "news/view",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () => import("../views/admin/certification/Index.vue"),
      },
      {
        path: "validaciones/:entityId/periodo/:periodYear",
        name: "admin-show-certification-entity",
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "certification/view",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () =>
          import(
            /* webpackChunkName: "admin" */ "../views/admin/certification/CertificationEntity.vue"
          ),
        props: true,
      },
      // Change password
      {
        path: "editar/perfil",
        name: "change-password",
        meta: {
          middlewares: [authorizationMiddleware],
        },
        component: () => import("../views/admin/ChangePassword.vue"),
      },

      // List citizen information request NEED PERMISSIONS
      {
        path: "solicitudes-informacion",
        name: "admins-show-information-request",
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "respond-request/view",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () => import("../views/admin/information-request/Index.vue"),
      },
      {
        path: "/responder-solicitud/:requestId",
        name: "admins-information-request-respond",
        component: () =>
          import(
            /* webpackChunkName: "request" */ "../views/admin/information-request/Create.vue"
          ),
        props: true,
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "respond-request/view",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
      },
      // View complete response
      {
        path: ":requestId/ver-respuesta-solicitud",
        name: "admins-information-request-respond-view",
        component: () =>
          import(
            /* webpackChunkName: "request" */ "../views/admin/information-request/View.vue"
          ),
        props: true,
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "respond-request/view",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
      },
      // Show Chart
      {
        path: "ver-grafica-sentimiento",
        name: "admins-information-request-chart",
        component: () =>
          import(
            /* webpackChunkName: "request" */ "../views/admin/information-request/ShowChart.vue"
          ),
        props: true,
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "graph/view",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
      },
      {
        path: "perfil-imagen",
        name: "admin-oficial-image-profile",
        component: () =>
          import(
            /* webpackChunkName: "admin" */ "@/views/admin/user/profile-picture/Show.vue"
          ),
        props: true,
        meta: {
          middlewares: [authorizationMiddleware],
          // permissions: new Map([
          //   [
          //     "graph/view",
          //     { userRoleId: () => store.state.authUser?.user?.roleId },
          //   ],
          // ]),
        },
      },
      {
        path: "reporte-anual-solicitudes",
        name: "admin-annual-information-report",
        component: () =>
          import(
            /* webpackChunkName: "admin" */ "@/views/admin/annual-information-report/Index.vue"
          ),
        props: true,
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "graph/view",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
      },
        /*
        * Budget execution account routes
        * */
      {
        path: "cuentas-ejecucion-presupuestaria",
        name: "admin-budget-execution-accounts",
        component: () =>
            import(
                /* webpackChunkName: "admin" */ "@/views/admin/budget-execution-account/Index.vue"
                ),
        props: true,
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "graph/view",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
      },

      {
        path: "crear-cuenta-ejecucion-presupuestaria",
        name: "admin-create-budget-execution-account",
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "budget-execution-accounts/create",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () => import("../views/admin/budget-execution-account/Create.vue"),
      },

      {
        path: ":budgetExecutionAccountId/cuenta-ejecucion-presupuestaria",
        name: "admin-edit-budget-execution-account",
        props: true,
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "budget-execution-accounts/edit",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () => import("../views/admin/budget-execution-account/Edit.vue"),
      },


      /*
* Missing submission routes
* */
      {
        path: "documentaciones-sin-entregar",
        name: "admin-missing-submission",
        component: () =>
            import(
                /* webpackChunkName: "admin" */ "@/views/admin/missing-submission/Index.vue"
                ),
        props: true,
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "missing-submission/view",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
      },

      {
        path: "documentaciones-sin-entregar/mensaje",
        name: "admin-edit-message-missing-submission",
        props: true,
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "missing-submission/edit",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () => import("../views/admin/missing-submission/Edit.vue"),
      },


      /**
       * Micro sites Routes
       */
      {
        path: "micro-sitios",
        name: "admin-show-micro-sites",
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "microsites/view",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () => import("../views/admin/micro-sites/Index.vue"),
      },

      {
        path: "crear-micro-sitios",
        name: "admin-create-micro-sites",
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "microsites/create",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () => import("../views/admin/micro-sites/Create.vue"),
      },
      {
        path: ":microSiteId/editar-micro-sitios",
        name: "admin-edit-micro-sites",
        props: true,
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "microsites/edit",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () => import("../views/admin/micro-sites/Edit.vue"),
      },
      {
        path: ":microSiteId/eliminar-micro-sitios",
        name: "admin-delete-micro-sites",
        props: true,
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "microsites/delete",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () => import("../views/admin/micro-sites/Delete.vue"),
      },

      /**
       * Advertisements routes
       */
      {
        path: "anuncios",
        name: "admin-show-advertisements",
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "advertisement/view",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () => import("../views/admin/advertisement/Index.vue"),
      },
      {
        path: "crear-advertisement",
        name: "admin-create-advertisement",
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "advertisement/create",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () => import("../views/admin/advertisement/Create.vue"),
      },
      {
        path: ":advertisementId/editar-anuncio",
        name: "admin-edit-advertisement",
        props: true,
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "advertisement/create",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () => import("../views/admin/advertisement/Edit.vue"),
      },
      {
        path: ":advertisementId/ver-anuncio",
        name: "admin-show-advertisement",
        props: true,
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "advertisement/view-any",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () => import("../views/admin/advertisement/Show.vue"),
      },
      {
        path: ":advertisementId/eliminar-anuncio",
        name: "admin-delete-advertisement",
        props: true,
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "advertisement/delete",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () => import("../views/admin/advertisement/Delete.vue"),
      },

      /**
       * Carousel routes
       */
      {
        path: "carrusel",
        name: "admin-show-carousels",
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "carousel/view",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () => import("../views/admin/carousel/Index.vue")
      },
      {
        path: "crear-carrusel",
        name: "admin-create-carousel",
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "carousel/create",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () => import("../views/admin/carousel/Create.vue"),
      },
      {
        path: ":carouselId/editar-carrusel",
        name: "admin-edit-carousel",
        props: true,
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "carousel/edit",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () => import("../views/admin/carousel/Edit.vue"),
      },
      {
        path: ":carouselId/eliminar-carrusel",
        name: "admin-delete-carousel",
        props: true,
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "carousel/delete",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () => import("../views/admin/carousel/Delete.vue"),
      },
      {
        path: "logos",
        name: "admin-show-logo",
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([
            [
              "logo/view",
              { userRoleId: () => store.state.authUser?.user?.roleId },
            ],
          ]),
        },
        component: () => import("../views/admin/logo/Index.vue"),
      },

      UserRoutes,
      EntityRoutes,
      {
        path: "verifica-planilla",
        name: "admin-payroll-verification",
        component: () =>
          import(
            /* webpackChunkName: "admin" */ "../views/admin/PayrollVerification.vue"
          ),
        meta: {
          middlewares: [authorizationMiddleware],
          permissions: new Map([["payroll-verification", {}]]),
        },
      },
    ],
  } /***  End Of Private Routes ***/,
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

export function bindAppToRouter(vm: Vue): void {
  appVm = vm;
}

/**
 * Sets the page title from route.meta if it is set or default specified in the
 * function.
 *
 * @param to - The route the app is going to.
 *
 */
function setRouteTitle(to: RouteLocationNormalized) {
  let title = "Plataforma de monitoreo de transparencia";
  if (typeof to.meta?.title === "string") {
    title = to.meta.title;
  }
  document.title = title;
}

router.beforeEach(async (to, from) => {
  setRouteTitle(to);

  const permissions = new Map();
  let middlewares: Middleware[] = [];
  to.matched.forEach((route: RouteRecordNormalized) => {
    const meta = route.meta;
    if (Array.isArray(meta.middlewares)) {
      middlewares = middlewares.concat(meta.middlewares as Array<Middleware>);
    }
    if (meta.permissions instanceof Map) {
      meta.permissions.forEach((v, k) => permissions.set(k, v));
    }
  });
  log(
    "beforeEach route's permissions %O middlewares %O",
    permissions,
    middlewares
  );
  if (middlewares.length == 0) {
    return;
  }
  to.meta.permissions = permissions;
  to.meta.middleware = middlewares;
  const context = {
    to,
    from,
    store: store as Store<StoreState>,
  };
  const middlewareChain = new MiddlewareChain(context, middlewares);
  const middlewareResult = await middlewareChain.handle();
  if (middlewareResult.action == "reject") {
    const redirectTo = middlewareResult.redirect ?? "/";
    return redirectTo;
  } else {
    return;
  }
});

export default router;
export { appVm };
